import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiClient } from '@/services/api'
import type { MatchView, Series } from '@/types/api'
import { Icon, ICONS } from '@/lib/icons'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { SharedEmptyState } from './Styles'
import { EditMatchDialog } from './EditMatchDialog'
import { exportMatchesToCSV, type MatchCSVData } from '@/utils/csvExport'
import { useMatchPermissions } from '@/hooks/useMatchPermissions'

interface MatchesListProps {
  seriesId: string
  seriesStartDate?: string
  seriesEndDate?: string
  seriesName?: string
  refreshKey?: number
  series?: Series  // Passed through to EditMatchDialog for scoring profile detection
}

export function MatchesList({ seriesId, seriesStartDate, seriesEndDate, seriesName, refreshKey, series }: MatchesListProps) {
  const { t } = useTranslation()
  const { canModifyMatch } = useMatchPermissions()
  const [matches, setMatches] = useState<MatchView[]>([])
  const [loading, setLoading] = useState(true)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [nextPageToken, setNextPageToken] = useState<string | undefined>()
  const [editingMatch, setEditingMatch] = useState<MatchView | null>(null)
  const [exporting, setExporting] = useState(false)

  const loadMatches = useCallback(async (pageToken?: string) => {
    try {
      setLoading(true)
      const response = await apiClient.listMatches({
        seriesId: seriesId,
        pageSize: 20,
        cursorAfter: pageToken
      }, 'matches-list')

      if (pageToken) {
        setMatches(prev => [...prev, ...response.items])
      } else {
        setMatches(response.items)
      }
      setNextPageToken(response.endCursor)
      setHasNextPage(response.hasNextPage)
    } catch (error: unknown) {
      toast.error((error as Error).message || t('error.generic'))
    } finally {
      setLoading(false)
    }
  }, [seriesId, t])

  useEffect(() => {
    loadMatches()
  }, [loadMatches, refreshKey])

  const determineOutcome = (match: MatchView): 'A' | 'B' | 'DRAW' => {
    switch (match.outcome) {
      case 'OUTCOME_PLAYER_A_WIN':
        return 'A'
      case 'OUTCOME_PLAYER_B_WIN':
        return 'B'
      case 'OUTCOME_DRAW':
        return 'DRAW'
    }
    if (match.scoreA > match.scoreB) return 'A'
    if (match.scoreB > match.scoreA) return 'B'
    return 'DRAW'
  }

  const getWinnerLabel = (match: MatchView) => {
    const outcome = determineOutcome(match)
    if (outcome === 'A') return match.playerAName
    if (outcome === 'B') return match.playerBName
    return t('matches.draw')
  }

  // Returns sport-appropriate score header: "Goals" for hockey/foosball, "Sets" for racket sports
  const getScoreColumnHeader = () => {
    const sport = series?.sport
    if (sport === 'SPORT_TABLE_HOCKEY' || sport === 'SPORT_FOOSBALL') {
      return t('groupStage.view.goals', 'Mål')
    }
    return t('groupStage.view.sets', 'Set')
  }

  const handleMatchUpdated = (updatedMatch: MatchView) => {
    setMatches(prev => prev.map(m => m.id === updatedMatch.id ? updatedMatch : m))
  }

  const handleMatchDeleted = (matchId: string) => {
    setMatches(prev => prev.filter(m => m.id !== matchId))
  }

  const handleEditMatch = (match: MatchView) => {
    setEditingMatch(match)
  }

  const handleExportCSV = async () => {
    if (exporting) return

    setExporting(true)
    try {
      const allMatches: MatchView[] = []
      let cursor: string | undefined
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.listMatches({
          seriesId,
          pageSize: 100,
          cursorAfter: cursor
        }, 'matches-export')

        allMatches.push(...response.items)

        if (response.hasNextPage && response.endCursor) {
          cursor = response.endCursor
        } else {
          hasMore = false
        }
      }

      const csvData: MatchCSVData[] = allMatches.map((match, index) => ({
        sequence: index + 1,
        playerA: match.playerAName,
        scoreA: match.scoreA,
        scoreB: match.scoreB,
        playerB: match.playerBName,
        winner: getWinnerLabel(match),
        date: new Date(match.playedAt).toLocaleDateString(),
        time: new Date(match.playedAt).toLocaleTimeString('sv-SE', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        playedAt: match.playedAt
      }))

      exportMatchesToCSV(csvData, seriesName)
      toast.success(t('matches.exportSuccess'))
    } catch (error: unknown) {
      toast.error((error as Error).message || t('matches.exportError'))
    } finally {
      setExporting(false)
    }
  }

  if (loading && matches.length === 0) {
    return <LoadingSpinner />
  }

  if (matches.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <SharedEmptyState>
            <Icon icon={ICONS.tournament} width={48} height={48} />
            <h3>{t('matches.empty')}</h3>
            <p>{t('matches.emptyDescription')}</p>
          </SharedEmptyState>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t('matches.title')}</CardTitle>
          {matches.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="flex items-center gap-2"
              disabled={exporting}
            >
              <Icon
                icon={ICONS.export}
                width={16}
                height={16}
                className={exporting ? 'animate-spin' : undefined}
              />
              {exporting ? t('matches.exporting') : t('matches.exportCSV')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>{t('matches.player_a')}</TableHead>
                  <TableHead className="text-center">{getScoreColumnHeader()}</TableHead>
                  <TableHead>{t('matches.player_b')}</TableHead>
                  <TableHead>{t('matches.played_at')}</TableHead>
                  <TableHead className="text-center">{t('matches.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matches.map((match, index) => {
                  const outcome = determineOutcome(match)
                  const playerAWon = outcome === 'A'
                  const playerBWon = outcome === 'B'
                  const isDraw = outcome === 'DRAW'
                  const scoreAClass = playerAWon
                    ? 'font-bold text-foreground'
                    : isDraw
                      ? 'font-semibold text-warning'
                      : 'text-muted-foreground'
                  const scoreBClass = playerBWon
                    ? 'font-bold text-foreground'
                    : isDraw
                      ? 'font-semibold text-warning'
                      : 'text-muted-foreground'

                  return (
                    <TableRow key={match.id}>
                      <TableCell className="text-center text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span className={playerAWon ? 'font-semibold' : ''}>
                            {match.playerAName}
                          </span>
                          {playerAWon ? (
                            <Icon icon={ICONS.checkCircle} width={16} height={16} className="text-success" />
                          ) : isDraw ? (
                            <Icon icon={ICONS.draw} width={16} height={16} className="text-warning" />
                          ) : (
                            <Icon icon={ICONS.closeCircle} width={16} height={16} className="text-destructive" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span className={scoreAClass}>
                            {match.scoreA}
                          </span>
                          <span className="text-muted-foreground">-</span>
                          <span className={scoreBClass}>
                            {match.scoreB}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span className={playerBWon ? 'font-semibold' : ''}>
                            {match.playerBName}
                          </span>
                          {playerBWon ? (
                            <Icon icon={ICONS.checkCircle} width={16} height={16} className="text-success" />
                          ) : isDraw ? (
                            <Icon icon={ICONS.draw} width={16} height={16} className="text-warning" />
                          ) : (
                            <Icon icon={ICONS.closeCircle} width={16} height={16} className="text-destructive" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex flex-col items-start">
                          <span className="text-sm">
                            {new Date(match.playedAt).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(match.playedAt).toLocaleTimeString('sv-SE', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          {canModifyMatch(series?.clubId || '', match.playerAId, match.playerBId) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMatch(match)}
                              className="h-8 w-8 p-0"
                            >
                              <Icon icon={ICONS.edit} width={16} height={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View - Ultra Compact */}
          <div className="md:hidden space-y-2 p-4">
            {matches.map((match, index) => {
              const outcome = determineOutcome(match)
              const playerAWon = outcome === 'A'
              const playerBWon = outcome === 'B'
              const isDraw = outcome === 'DRAW'
              const score = `${match.scoreA}-${match.scoreB}`

              return (
                <div 
                  key={match.id} 
                  className="bg-card rounded-lg p-3 shadow-sm border border-border"
                  onClick={() => canModifyMatch(series?.clubId || '', match.playerAId, match.playerBId) && handleEditMatch(match)}
                >
                  {/* Row 1: Match number + Date/Time + Edit button */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">#{index + 1}</span>
                      <span>•</span>
                      <span>
                        {new Date(match.playedAt).toLocaleDateString('sv-SE', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                      <span>
                        {new Date(match.playedAt).toLocaleTimeString('sv-SE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    {canModifyMatch(series?.clubId || '', match.playerAId, match.playerBId) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditMatch(match)
                        }}
                        className="h-6 w-6 p-0 hover:bg-accent"
                      >
                        <Icon icon={ICONS.edit} width={14} height={14} />
                      </Button>
                    )}
                  </div>
                  
                  {/* Row 2: Player A + score + Player B (preserve order, bold winner) */}
                  {isDraw ? (
                    <div className="text-sm text-foreground truncate">
                      <span className="font-semibold">{match.playerAName}</span>
                      <span className="mx-2 font-bold text-base text-warning">{score}</span>
                      <span className="font-semibold">{match.playerBName}</span>
                      <span className="ml-2 text-warning">({t('matches.draw')})</span>
                    </div>
                  ) : (
                    <div className="text-sm text-foreground truncate">
                      <span className={playerAWon ? "font-semibold" : "text-muted-foreground"}>{match.playerAName}</span>
                      <span className="mx-2 font-bold text-base">{score}</span>
                      <span className={playerBWon ? "font-semibold" : "text-muted-foreground"}>{match.playerBName}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => loadMatches(nextPageToken)}
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" /> : t('common.showMore')}
          </Button>
        </div>
      )}

      <EditMatchDialog
        match={editingMatch}
        isOpen={!!editingMatch}
        onClose={() => setEditingMatch(null)}
        onMatchUpdated={handleMatchUpdated}
        onMatchDeleted={handleMatchDeleted}
        seriesStartDate={seriesStartDate}
        seriesEndDate={seriesEndDate}
        series={series}
      />
    </div>
  )
}

